import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ReactComponent as Logo } from "../../assets/img/logo-icon.svg";
import { Spinner, TableSearch } from "components/elements";
import { TrendingRow } from "components/layout/TrendingRow";
import { throttle } from "hooks/useThrottled";
import { useUserTimezone } from "hooks/useUserTimezone";
import { trpc } from "lib/trpc";
import { RssFeedCard } from "./RssFeedCard";
import RssFeedSection from "./RssFeedSection";

export const RssList = () => {
    const [searchValue, setSearchValue] = useState("");
    const [imgError, setImgError] = useState(false);
    const navigate = useNavigate();
    const timeZone = useUserTimezone();
    const { data } = trpc.rss.listSources.useQuery();

    const latestQuery = trpc.rss.latestEntries.useQuery({
        searchTerm: searchValue,
    });

    const { data: sources } = trpc.rss.listSources.useQuery();
    const rssFeeds = sources?.feeds ?? [];

    const latestItems = latestQuery.data?.entries ?? [];
    const [topItem, ...rest] = latestItems;
    const fourBelow = rest.slice(0, 4);

    // this commented code will allow a shift to categories (sports, business) from sources (bbc etc) if need be
    //
    // (skip nulls)
    // const uniqueCategories = [
    //     ...new Set(rssFeeds.map((feed) => feed.category).filter(Boolean)),
    // ] as string[];

    return (
        <div className="mx-auto w-full items-center justify-center pt-6">
            <TrendingRow filterType="feedArticle" title="Trending Articles" limit={5} />
            {/* Latest News Section */}
            <div className="mb-3 flex w-full flex-row items-baseline justify-between">
                <h2 className="mb-6 font-sans text-[28px] font-semibold text-[#424242]">
                    Latest News
                </h2>
                <TableSearch value={searchValue} onChange={throttle(setSearchValue, 300)} />
            </div>
            {!latestQuery.isPending ? (
                <>
                    {topItem && (
                        <section className="mb-6">
                            <div className="">
                                <a
                                    href={topItem.url}
                                    target="_blank"
                                    rel="noopener noreferrer "
                                    className="text-inherit no-underline hover:no-underline focus:outline-none"
                                >
                                    <div className="flex flex-row items-start">
                                        <div className="min-w-[460px] flex-shrink-0">
                                            <div className="h-72 overflow-hidden rounded-xl bg-gray-100">
                                                {topItem.imageUrl && !imgError ? (
                                                    <img
                                                        src={topItem.imageUrl}
                                                        alt=""
                                                        onError={() => setImgError(true)}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <>
                                                        <Logo className="h-full w-full object-cover" />
                                                        {/*<div className="flex h-full w-full items-center justify-center bg-gray-100 text-sm text-gray-400"></div>*/}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-grow flex-col gap-2 p-6">
                                            <p className="mb-1 font-sans text-[16px] font-medium leading-[152%] text-[#424242]">
                                                {topItem.feedSourceTitle}{" "}
                                                <span className="">
                                                    -{" "}
                                                    {new Date(
                                                        topItem.publishedAt,
                                                    ).toLocaleDateString("en-US", {
                                                        year: "numeric",
                                                        month: "long",
                                                        day: "numeric",
                                                        timeZone,
                                                    })}
                                                </span>
                                            </p>
                                            <h3 className="mb-2 font-sans text-[34px] font-semibold leading-[125%] text-[#424242]">
                                                {topItem.title}
                                            </h3>
                                            <p className="line-clamp-4 mt-2 font-sans text-[18px] font-medium leading-[140%] text-[#424242]">
                                                {topItem.excerpt}
                                            </p>
                                        </div>
                                    </div>
                                </a>
                            </div>

                            {fourBelow.length > 0 && (
                                <>
                                    <div className="flex justify-between pt-9">
                                        <h2 className="mb-6 font-sans text-[24px] font-semibold text-[#424242]">
                                            Trending news
                                        </h2>

                                        <span
                                            className="cursor-pointer font-sans text-[20px] font-normal text-[#37598A] underline hover:underline"
                                            onClick={() => {
                                                navigate({
                                                    to: "/rss-feeds/category/$categoryName",
                                                    params: { categoryName: "latest" },
                                                });
                                                setTimeout(() => {
                                                    const el =
                                                        document.getElementById(
                                                            "scrollable-content",
                                                        );
                                                    el?.scrollTo({ top: 0, behavior: "smooth" });
                                                }, 0);
                                            }}
                                        >
                                            See all
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                                        {fourBelow.map((article) => (
                                            <div key={article.id}>
                                                <RssFeedCard
                                                    articleId={article.id}
                                                    title={article.title}
                                                    description={article.excerpt}
                                                    date={article.publishedAt?.split("T")[0] ?? ""}
                                                    link={article.url ?? ""}
                                                    imageUrl={article.imageUrl ?? ""}
                                                    feedSourceTitle={article.feedSourceTitle}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </section>
                    )}

                    {/* Feed under latest with other news */}

                    {rssFeeds.map((feed, index) => (
                        <div className="w-full" key={feed.id}>
                            <RssFeedSection searchValue={searchValue} feed={feed} />
                        </div>
                    ))}

                    {latestItems.length === 0 && (
                        <div className="flex h-96 items-center justify-center">
                            <p className="text-sm">No results found.</p>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className="flex h-96 items-center justify-center">
                        <Spinner className="h-8 w-8" />
                    </div>
                </>
            )}
        </div>
    );
};
